import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import PageHeader from "@/components/common/PageHeader";
import { governanceService } from "@/services/governance.service";
import { tenantService } from "@/services/tenant.service";
import { RoleFormModal } from "@/components/governance/RoleFormModal";
import { PermissionFormModal } from "@/components/governance/PermissionFormModal";
import type { RoleItem, PermissionItem } from "@/types";
import toast from "react-hot-toast";
import {
  Plus,
  Shield,
  Key,
  MoreVertical,
  Edit2,
  Trash2,
  Globe,
  Building2,
  Search,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";

function RolesPermissionsPage({ embedded = false }: { embedded?: boolean }) {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<"roles" | "permissions">("roles");
  const [tenantId, setTenantId] = useState<string>("");
  const [includeGlobal] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [permissionSearchQuery, setPermissionSearchQuery] = useState("");

  // Modal states
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [permissionModalOpen, setPermissionModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<RoleItem | null>(null);
  const [selectedPermission, setSelectedPermission] =
    useState<PermissionItem | null>(null);

  const { data: tenants } = useQuery({
    queryKey: ["admin-tenants", "role-selector"],
    queryFn: () => tenantService.list({ page: 1, limit: 100 }),
  });

  const { data: permissions, isLoading: isLoadingPermissions } = useQuery({
    queryKey: ["admin-permissions"],
    queryFn: () => governanceService.listPermissions(),
  });

  const { data: roles, isLoading: isLoadingRoles } = useQuery({
    queryKey: ["admin-roles", tenantId, includeGlobal],
    queryFn: () =>
      governanceService.listRoles({
        tenantId: tenantId || undefined,
        includeGlobal,
      }),
  });

  const filteredRoles = useMemo(() => {
    return (roles ?? []).filter(
      (role) =>
        role.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        role.description?.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [roles, searchQuery]);

  const groupedPermissions = useMemo(() => {
    const grouped: Record<string, PermissionItem[]> = {};
    (permissions ?? []).forEach((perm) => {
      const group = perm.group || "General";
      if (!grouped[group]) grouped[group] = [];
      grouped[group].push(perm);
    });
    return grouped;
  }, [permissions]);

  const filteredGroupedPermissions = useMemo(() => {
    if (!permissionSearchQuery.trim()) return groupedPermissions;

    const result: Record<string, PermissionItem[]> = {};
    Object.entries(groupedPermissions).forEach(([group, perms]) => {
      const filtered = perms.filter(
        (p) =>
          p.key.toLowerCase().includes(permissionSearchQuery.toLowerCase()) ||
          p.description
            ?.toLowerCase()
            .includes(permissionSearchQuery.toLowerCase()),
      );
      if (filtered.length > 0) {
        result[group] = filtered;
      }
    });
    return result;
  }, [groupedPermissions, permissionSearchQuery]);

  // Mutations
  const { mutateAsync: deleteRole } = useMutation({
    mutationFn: (id: string) => governanceService.deleteRole(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-roles"] });
      toast.success("Role deleted successfully");
    },
  });

  const { mutateAsync: deletePermission } = useMutation({
    mutationFn: (id: string) => governanceService.deletePermission(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-permissions"] });
      toast.success("Permission deleted successfully");
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.message || "Failed to delete permission",
      );
    },
  });

  const handleOpenRoleModal = (role?: RoleItem) => {
    setSelectedRole(role ?? null);
    setRoleModalOpen(true);
  };

  const handleCloseRoleModal = () => {
    setRoleModalOpen(false);
    setSelectedRole(null);
  };

  const handleOpenPermissionModal = (permission?: PermissionItem) => {
    setSelectedPermission(permission ?? null);
    setPermissionModalOpen(true);
  };

  const handleClosePermissionModal = () => {
    setPermissionModalOpen(false);
    setSelectedPermission(null);
  };

  const handleDeleteRole = async (role: RoleItem) => {
    if (role.isSystem) {
      toast.error("Cannot delete system roles");
      return;
    }
    if (
      window.confirm(`Are you sure you want to delete the role "${role.name}"?`)
    ) {
      await deleteRole(role.id);
    }
  };

  const handleDeletePermission = async (permission: PermissionItem) => {
    if (
      window.confirm(
        `Are you sure you want to delete the permission "${permission.key}"?`,
      )
    ) {
      await deletePermission(permission.id);
    }
  };

  return (
    <div className={embedded ? "space-y-6" : "space-y-10 pb-20"}>
      {!embedded && (
        <PageHeader
          title="Role Registry"
          subtitle="Manage administrative authority and permission protocols across the ecosystem."
        />
      )}

      {/* Tabs */}
      <div className="flex items-center gap-1 p-1 rounded-2xl bg-slate-100 border border-slate-200 w-fit">
        <button
          onClick={() => setActiveTab("roles")}
          className={`px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all duration-300 flex items-center gap-2 ${
            activeTab === "roles"
              ? "bg-blue-600 text-white shadow-lg shadow-blue-500/20"
              : "text-slate-500 hover:text-slate-900"
          }`}
        >
          <Shield className="w-4 h-4" />
          Managed Roles
        </button>
        <button
          onClick={() => setActiveTab("permissions")}
          className={`px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all duration-300 flex items-center gap-2 ${
            activeTab === "permissions"
              ? "bg-blue-600 text-white shadow-lg shadow-blue-500/20"
              : "text-slate-500 hover:text-slate-900"
          }`}
        >
          <Key className="w-4 h-4" />
          Permission Keys
        </button>
      </div>

      {activeTab === "roles" ? (
        <section className="space-y-6 animate-slide-up">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3 bg-white border border-slate-200 px-4 py-2 rounded-2xl w-full max-w-md group focus-within:border-blue-500/50 transition-all shadow-sm">
              <Search className="w-4 h-4 text-slate-400 group-focus-within:text-blue-500" />
              <input
                type="text"
                placeholder="Search roles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-transparent border-none outline-none text-sm text-slate-900 placeholder-slate-400 w-full font-medium"
              />
            </div>

            <div className="flex items-center gap-3">
              <select
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
                className="bg-white border border-slate-200 px-4 py-2.5 rounded-xl text-xs font-bold text-slate-700 outline-none focus:border-blue-500/50 transition-all shadow-sm"
              >
                <option value="">All Tenants</option>
                {tenants?.data.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>

              <button
                onClick={() => handleOpenRoleModal()}
                className="bg-blue-600 p-2.5 rounded-xl text-white hover:bg-blue-700 transition-all active:scale-95 shadow-lg shadow-blue-500/20"
              >
                <Plus className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {isLoadingRoles ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="h-56 rounded-[2rem] border border-slate-100 bg-slate-50 animate-pulse"
                />
              ))
            ) : filteredRoles.length ? (
              filteredRoles.map((role) => (
                <div
                  key={role.id}
                  className="group relative flex flex-col justify-between p-8 rounded-[2.5rem] bg-white border border-slate-100 transition-all hover:bg-slate-50 hover:border-slate-200 shadow-[0_10px_40px_-15px_rgba(0,0,0,0.05)] hover:shadow-[0_20px_60px_-15px_rgba(0,0,0,0.1)]"
                >
                  <div className="absolute top-0 right-0 p-6">
                    <div className="bg-slate-50 p-2 rounded-xl text-slate-400 opacity-0 group-hover:opacity-100 transition-all cursor-pointer hover:bg-slate-100 hover:text-slate-600">
                      <MoreVertical className="w-4 h-4" />
                    </div>
                  </div>

                  <div>
                    <div className="mb-6">
                      <div className="flex items-center gap-2 mb-3">
                        {role.tenantId ? (
                          <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-lg bg-cyan-50 text-cyan-600 text-[10px] font-black uppercase tracking-widest border border-cyan-100">
                            <Building2 className="w-3 h-3" />
                            Tenant
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-lg bg-blue-50 text-blue-600 text-[10px] font-black uppercase tracking-widest border border-blue-100">
                            <Globe className="w-3 h-3" />
                            Global
                          </div>
                        )}
                        {role.isSystem && (
                          <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-lg bg-slate-100 text-slate-500 text-[10px] font-black uppercase tracking-widest border border-slate-200">
                            System
                          </div>
                        )}
                      </div>
                      <h4 className="text-xl font-black text-slate-900 group-hover:text-blue-600 transition-colors uppercase tracking-tight">
                        {role.name}
                      </h4>
                      <p className="text-xs font-medium text-slate-500 mt-2 line-clamp-2 leading-relaxed">
                        {role.description || "No tactical overview provided."}
                      </p>
                    </div>

                    <div className="flex items-center gap-4 text-slate-500 font-bold mb-6">
                      <div className="flex items-center gap-1.5">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                        <span className="text-xs text-slate-950 font-black">
                          {role.permissions.length}
                        </span>
                        <span className="text-[10px] uppercase tracking-widest text-slate-400 font-black">
                          Permissions
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 pt-6 border-t border-slate-100">
                    <button
                      onClick={() => handleOpenRoleModal(role)}
                      className="flex-1 py-2.5 rounded-xl bg-blue-50 text-blue-600 text-xs font-black uppercase tracking-widest hover:bg-blue-600 hover:text-white transition-all border border-blue-100"
                    >
                      Configure
                    </button>
                    {!role.isSystem && (
                      <button
                        onClick={() => handleDeleteRole(role)}
                        className="p-2.5 rounded-xl bg-rose-50 text-rose-500 hover:bg-rose-500 hover:text-white transition-all border border-rose-100"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-full py-20 text-center rounded-[2.5rem] border border-dashed border-slate-200 bg-slate-50/50">
                <AlertCircle className="w-10 h-10 text-slate-300 mx-auto mb-4" />
                <p className="text-sm text-slate-500 font-medium tracking-wide">
                  No managed roles found matching your protocols.
                </p>
              </div>
            )}
          </div>
        </section>
      ) : (
        <section className="animate-slide-up space-y-8">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 bg-white border border-slate-200 px-4 py-2 rounded-2xl w-full max-w-md group focus-within:border-blue-500/50 transition-all shadow-sm">
              <Search className="w-4 h-4 text-slate-400 group-focus-within:text-blue-500" />
              <input
                type="text"
                placeholder="Search permission keys..."
                value={permissionSearchQuery}
                onChange={(e) => setPermissionSearchQuery(e.target.value)}
                className="bg-transparent border-none outline-none text-sm text-slate-950 placeholder-slate-400 w-full font-medium"
              />
            </div>
            <button
              onClick={() => handleOpenPermissionModal()}
              className="bg-slate-900 px-6 py-2.5 rounded-xl text-white text-xs font-black uppercase tracking-widest hover:bg-slate-800 transition-all shadow-lg active:scale-95"
            >
              Create Key
            </button>
          </div>

          <div className="grid gap-8">
            {isLoadingPermissions ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="h-32 rounded-[2rem] border border-slate-100 bg-slate-50 animate-pulse"
                />
              ))
            ) : Object.keys(filteredGroupedPermissions).length ? (
              Object.entries(filteredGroupedPermissions).map(
                ([group, perms]) => (
                  <div key={group} className="space-y-4">
                    <div className="flex items-center gap-3 px-2">
                      <div className="h-px flex-1 bg-slate-100" />
                      <h5 className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400">
                        {group}
                      </h5>
                      <div className="h-px flex-1 bg-slate-100" />
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                      {perms.map((perm) => (
                        <div
                          key={perm.id}
                          className="group relative p-5 rounded-2xl bg-white border border-slate-100 hover:border-blue-500/30 hover:shadow-md transition-all"
                        >
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <code className="text-[10px] font-black text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                              {perm.key}
                            </code>
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={() => handleOpenPermissionModal(perm)}
                                className="p-1 rounded bg-slate-50 text-slate-400 hover:text-blue-600"
                              >
                                <Edit2 className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => handleDeletePermission(perm)}
                                className="p-1 rounded bg-slate-50 text-slate-400 hover:text-rose-600"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                          <p className="text-[10px] font-medium text-slate-500 leading-relaxed">
                            {perm.description || "System protocol key."}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                ),
              )
            ) : (
              <div className="py-20 text-center rounded-[2.5rem] border border-dashed border-slate-200 bg-slate-50/50">
                <AlertCircle className="w-10 h-10 text-slate-300 mx-auto mb-4" />
                <p className="text-sm text-slate-500 font-medium tracking-wide">
                  No permission keys registered.
                </p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Modals */}
      {roleModalOpen && (
        <RoleFormModal role={selectedRole} onClose={handleCloseRoleModal} />
      )}

      {permissionModalOpen && (
        <PermissionFormModal
          permission={selectedPermission}
          onClose={handleClosePermissionModal}
        />
      )}
    </div>
  );
}

export default RolesPermissionsPage;
